import React, { useContext, useEffect, useRef, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import {
  Autocomplete,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AnDateField from '../../../components/DateTime/DateField'
import API from '../../../services/API'

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')

// `existingPGD` edits a PGD's details in place. `replaces` uploads the
// reviewed version of a PGD as a new record (new document, fresh signatures)
// and archives the one it replaces.
const AddNewPGD = ({ onCloseSubmit, existingPGD, replaces }) => {
  const [fetchError, setFetchError] = useState(false)
  const [medicineOptions, setMedicineOptions] = useState([])
  const [medicines, setMedicines] = useState(
    existingPGD?.medicines ?? replaces?.medicines ?? []
  )
  const [title, setTitle] = useState(
    existingPGD?.title ?? replaces?.title ?? ''
  )
  const [version, setVersion] = useState(existingPGD?.version ?? '')
  const [versionChanges, setVersionChanges] = useState(
    existingPGD?.versionChanges ?? ''
  )
  const [date, setDate] = useState(existingPGD?.date ?? new Date())
  const [reviewDate, setReviewDate] = useState(existingPGD?.reviewDate ?? null)
  const [newFile, setNewFile] = useState(null)
  const newFileInput = useRef()
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/siteConfig/medicines')
      .then((res) => {
        if (res.status === 200) {
          setMedicineOptions(res.data)
        }
      })
      .catch(() => setFetchError(true))
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (existingPGD) {
      await API.put('/governance/pgd', {
        medicines: medicines.map((m) => m._id),
        title,
        version,
        versionChanges,
        date,
        reviewDate,
        _id: existingPGD._id,
      }).then((res) => {
        if (res.status === 200) {
          setToast({ message: 'PGD Updated' })
          onCloseSubmit({
            medicines: medicines.map((m) => m._id),
            title,
            version,
            versionChanges,
            date,
            reviewDate,
            _id: existingPGD._id,
          })
        }
      })
    } else {
      if (!isPdfFile(newFile)) {
        setToast({
          message: 'PGDs must be uploaded as PDF. In Word use File → Save As → PDF.',
          severity: 'error',
        })
        return
      }
      let s3url
      let s3key
      await API.post('/gets3uploadUrl', {
        folder: 'governance/pgds',
        extension: 'pdf',
      }).then((res) => {
        s3url = res.data.url
        s3key = res.data.key
        API.put(s3url, newFile, {
          headers: {
            'Content-Type': 'application/pdf',
          },
        }).then((res) => {
          if (res.status === 200) {
            API.post('/governance/pgd', {
              medicines: medicines.map((m) => m._id),
              title,
              version,
              versionChanges,
              date,
              reviewDate,
              docLink: s3key,
              replaces: replaces?._id,
            }).then((res) => {
              if (res.status === 200) {
                setToast({
                  message: replaces
                    ? 'New version uploaded - previous version archived'
                    : 'PGD Uploaded',
                })
                onCloseSubmit()
              } else {
                setToast({
                  message:
                    res.data?.error ||
                    'Unable to upload PGD. Please try again later.',
                  severity: 'error',
                })
              }
            })
          }
        })
      })
    }
  }

  return (
    <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
      <Grid item xs={12}>
        <Typography variant="title2">
          {replaces ? 'Upload a new version' : 'Add a new PGD'}
        </Typography>
        {replaces && (
          <Typography variant="body2" color="text.secondary">
            Staff will need to sign the new version. {replaces.title}
            {replaces.version ? ` (version ${replaces.version})` : ''} will be
            archived with its signatures kept on record.
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="PGD Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={medicineOptions}
          value={medicines}
          onChange={(e, value) => setMedicines(value)}
          renderInput={(props) => <TextField {...props} label="Medicines" />}
          getOptionLabel={(option) =>
            `${option.title}${
              option.constitution && ` - ${option.constitution}`
            }`
          }
          getOptionKey={(option) => option._id}
          isOptionEqualToValue={(opt, value) => opt._id === value._id}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          label="Version"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          value={versionChanges}
          fullWidth
          onChange={(e) => setVersionChanges(e.target.value)}
          label="Version Changes"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AnDateField
          date={date}
          fullWidth
          onChange={(newDate) => setDate(newDate)}
          label="Date"
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AnDateField
          fullWidth
          date={reviewDate}
          onChange={(newDate) => setReviewDate(newDate)}
          label="Review Date"
          required
        />
      </Grid>
      {!existingPGD && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="anMainBlue"
            onClick={() => newFileInput.current.click()}
            disabled={!!newFile}
            fullWidth
          >
            {newFile ? 'Uploaded' : 'Select File'}
          </Button>
          <input
            ref={newFileInput}
            type="file"
            style={{ display: 'none' }}
            accept="application/pdf,.pdf"
            onChange={(e) => {
              const file = e.target.files[0]
              e.target.value = ''
              if (file && !isPdfFile(file)) {
                setToast({
                  message:
                    'PGDs must be uploaded as PDF. In Word use File → Save As → PDF.',
                  severity: 'error',
                })
                return
              }
              setNewFile(file ?? null)
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Button
          disabled={!existingPGD && !newFile}
          type="submit"
          color="anGreen"
          fullWidth
          variant="contained"
        >
          Submit
        </Button>
      </Grid>
    </Grid>
  )
}

export default AddNewPGD
